import { Fragment } from 'react';
import { twMerge } from 'tailwind-merge';

import { Divider } from '../../../../core-components/Divider';

import type { FC } from 'react';

export type CruisePriceTableLineItem = {
  /** Display-ready date (formatting/locale handled by the consumer). */
  date: string;
  mainDescription: string;
  additionalDescription: string;
  participants: string[];
  price: string;
};

export type CruisePriceTableProps = {
  lineItems: CruisePriceTableLineItem[];
  priceTotal: string;
  /**
   * Translate function for the labels. Defaults to german
   */
  t?: (key: string) => string;
  className?: string;
};

export const CruisePriceTable: FC<CruisePriceTableProps> = ({
  lineItems,
  priceTotal,
  t = key => key,
  className,
}) => (
  <div className={twMerge('text-lg text-marine-high-emphasis', className)}>
    {lineItems.map((item, index) => (
      <Fragment key={index}>
        {/* One line item. From sm up: three columns (date+participants |
            descriptions | price). On xs: two columns — the descriptions wrap
            below the date+participants column (spacing/2 above), price stays
            top-right. No visible header row — column names are sr-only. */}
        <div className="grid grid-cols-[1fr_auto] gap-x-4 sm:grid-cols-[2fr_2fr_1fr]">
          <div className="flex flex-col gap-y-1">
            <div className="font-semibold">
              <span className="sr-only">{t('Datum')}: </span>
              <span>{item.date}</span>
            </div>
            <div>
              <span className="sr-only">{t('Reisende')}: </span>
              <span>{item.participants.join(', ')}</span>
            </div>
          </div>

          <div className="col-start-1 row-start-2 mt-2 flex flex-col gap-y-1 sm:col-start-2 sm:row-start-1 sm:mt-0">
            <div>
              <span className="sr-only">{t('Beschreibung')}: </span>
              <span>{item.mainDescription}</span>
            </div>
            <div>
              <span className="sr-only">{t('Zusatzinformation')}: </span>
              <span>{item.additionalDescription}</span>
            </div>
          </div>

          <div className="col-start-2 row-start-1 flex flex-col text-right sm:col-start-3">
            <div>
              <span className="sr-only">{t('Preis')}: </span>
              <span>{item.price}</span>
            </div>
          </div>
        </div>

        {index < lineItems.length - 1 && (
          <Divider color="secondary-20" className="my-6" />
        )}
      </Fragment>
    ))}

    <Divider color="secondary-40" className="my-6" />

    <div className="flex justify-between font-semibold">
      <span>{t('Gesamtpreis')}</span>
      <span>{priceTotal}</span>
    </div>
  </div>
);
